'use client'

import { useTranslations } from 'next-intl'
import { prepareTagSlugs } from '@/api/restaurants/get-restaurants-query'
import { Button } from '@/components/button'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'

/**
 * Shown in place of the result grid when the active filters match nothing.
 * Always offers an escape hatch: clearing the open-now toggle when it is the
 * sole cause, otherwise resetting search, tags and toggle at once.
 */
export const RestaurantCitySearchEmptyState = () => {
    const {
        query,
        setters: { setName, setTagSlugs, setOpenNow },
    } = useRestaurantCitySearchContext()
    const t = useTranslations()

    const searchTerm = query.name?.trim()
    const hasTags = Boolean(prepareTagSlugs(query.tagSlugs)?.length)

    // The unfiltered city list is never empty, so when only the toggle is
    // active it is the culprit and gets a tailored way out
    const isOpenNowOnly = Boolean(query.openNow) && !searchTerm && !hasTags

    const onReset = () => {
        setName('')
        setTagSlugs([])
        setOpenNow(false)
    }

    return (
        <div className="col-span-full flex flex-col items-center py-20 text-center">
            <span aria-hidden className="text-[40px] leading-none">
                {isOpenNowOnly ? '⏰' : '🔍'}
            </span>

            <p className="mt-4 text-h5">
                {isOpenNowOnly
                    ? t('restaurants.city.emptyState.openNowTitle')
                    : searchTerm
                      ? t('restaurants.city.emptyState.titleWithQuery', { query: searchTerm })
                      : t('restaurants.city.emptyState.title')}
            </p>

            <p className="mt-1 max-w-[400px] text-p-sm text-fg-tertiary">
                {isOpenNowOnly
                    ? t('restaurants.city.emptyState.openNowDescription')
                    : t('restaurants.city.emptyState.description')}
            </p>

            <Button
                shape="pill"
                styleType="secondary"
                className="mt-6"
                onClick={isOpenNowOnly ? () => setOpenNow(false) : onReset}
            >
                {isOpenNowOnly
                    ? t('restaurants.city.emptyState.openNowAction')
                    : t('restaurants.city.emptyState.reset')}
            </Button>
        </div>
    )
}
